import { Texture } from "../../../texture";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";

export class TextComponent extends Webgl2Component {
  private _text = "";
  private _textFontSize = 14;
  private static _PADDING_PX = 5;

  private _isEditing = false;
  private _caretIndex = 0;

  public get fontSize() {
    return this._textFontSize;
  }

  private _texture: Texture;
  public get texture() {
    return this._texture;
  }

  public get isEditing() {
    return this._isEditing;
  }

  private _canvas: HTMLCanvasElement;
  private _ctx: CanvasRenderingContext2D;

  constructor(text: string) {
    super();

    this._texture = new Texture();

    this._prepareCanvas();
    this.setText(text);
  }

  public setFontSize(fontSize: number) {
    this._textFontSize = Math.max(fontSize, 10);
    this._prepareText();
  }

  public setText(text: string) {
    this._text = text;
    this._prepareText();
  }

  public setEditing(value: boolean) {
    if (this._isEditing === value) return;
    this._isEditing = value;
    this._caretIndex = this._text.length;
    this._prepareText();
  }

  public setCaretIndex(index: number) {
    const next = Math.max(0, Math.min(index, this._text.length));
    if (this._caretIndex === next) return;
    this._caretIndex = next;
    if (this._isEditing) this._prepareText();
  }

  private _prepareText() {
    // NOTE: while editing, we still render empty label to show caret/border
    if (!this._text && !this._isEditing) return;

    this._setTextConfig();

    const padding = TextComponent._PADDING_PX;
    const textToMeasure = this._text || " ";
    const metrics = this._ctx.measureText(textToMeasure);

    // Note: adding 1px specially to fix text is close to edge from right side
    const width = metrics.width + padding * 2;
    const height = this._textFontSize + padding * 2;

    this._canvas.width = width;
    this._canvas.height = height;
    this.transform.setSize(width, height);
    this._clearCanvas();

    this._ctx.fillStyle = "#ffffff";
    this._ctx.fillRect(0, 0, width, height);

    this._setTextConfig();

    // Safari/WebKit can render canvas text with slightly different baselines (esp. digits).
    // To keep it visually centered across browsers, prefer real glyph bbox metrics when available.
    const ascent = (metrics as any).actualBoundingBoxAscent as number | undefined;
    const descent = (metrics as any).actualBoundingBoxDescent as number | undefined;
    const hasBboxMetrics =
      typeof ascent === "number" &&
      typeof descent === "number" &&
      Number.isFinite(ascent) &&
      Number.isFinite(descent) &&
      ascent + descent > 0;

    if (hasBboxMetrics) {
      this._ctx.textBaseline = "alphabetic";
      const y = height / 2 + (ascent - descent) / 2;
      this._ctx.fillText(this._text, padding, y);
    } else {
      // Fallback keeps previous behavior (center em-box via bottom baseline + symmetric padding).
      this._ctx.textBaseline = "bottom";
      this._ctx.fillText(this._text, padding, height - padding);
    }

    if (this._isEditing) {
      // Border
      this._ctx.strokeStyle = "#0099a1";
      this._ctx.lineWidth = 2;
      this._ctx.strokeRect(1, 1, width - 2, height - 2);

      const textBeforeCaret = this._text.slice(0, this._caretIndex);
      const caretX = padding + this._ctx.measureText(textBeforeCaret).width;
      this._ctx.beginPath();
      this._ctx.strokeStyle = "#363E5C";
      this._ctx.lineWidth = 1;
      this._ctx.moveTo(caretX, padding);
      this._ctx.lineTo(caretX, height - padding);
      this._ctx.stroke();
    }

    this._texture.loadSyncSource(this._canvas);
  }

  private _prepareCanvas() {
    const canvas = document.createElement("canvas");
    this._canvas = canvas;
    this._ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
  }

  private _setTextConfig() {
    // https://developer.mozilla.org/en-US/docs/Web/CSS/font
    this._ctx.font = `normal 400 ${this._textFontSize}px/${this._textFontSize}px 'Inter'`;
    this._ctx.fillStyle = "#363E5C";
    this._ctx.textBaseline = "alphabetic";
  }

  private _clearCanvas() {
    this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
  }

  public getRenderData() {
    if (!this._text && !this._isEditing) return;

    const material = Webgl2.materials.default;
    material.setTexture("u_texture0", this._texture);
    return { material };
  }
}

export default TextComponent;
